import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { VideoKycWrapper } from "./style";
import { videoKycInfo, videoKycSubmit } from "../../Utils/api";
import {
  CAMERA_GOOD_FRAMES,
  CAMERA_HINT_AFTER_MS,
  CAMERA_MIN_BRIGHTNESS,
  CHECK_INTERVAL_MS,
  MIC_HINT_AFTER_MS,
  MIC_SPEECH_LEVEL,
  MIC_SPEECH_TICKS,
  averageBrightness,
  getLocation,
  locationErrorMessage,
  mediaErrorMessage,
  micLevel,
} from "./deviceCheck";

const MAX_SECONDS = 90;
const LINK_CHECK_MS = 10000; // how often an open page asks whether its link still works
const BLOCKED_STATES = ["expired", "already_done", "invalid", "replaced"];

// Without a cap browsers record at about 2.5 Mbps, so 90 seconds is ~25 MB and the API (upload_max_filesize 8M)
// would refuse it. 500 kbps video + 48 kbps audio keeps a full-length recording near 6 MB.
const VIDEO_BITS_PER_SECOND = 500000;
const AUDIO_BITS_PER_SECOND = 48000;

const BLOCKED_MESSAGES = {
  invalid: "This link is not valid. Please Contact your credit manager for a new one.",
  expired: "This link has expired. Please ask your credit manager for a new one.",
  already_done: "Your Video KYC has already been submitted. Thank you!",
  replaced: "A newer Video KYC link has been sent to you. Please open the latest email from KuberMax and use that link.",
};

const MIME_CANDIDATES = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];

const formatTimer = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/**
 * Opened from the link a credit manager emails (no login, the signed token in the URL is the credential).
 *   check  -> the customer tests the camera and microphone; the next step is only offered when both work
 *   ready  -> the customer reads the script and records; the video is uploaded and the server stores it in S3
 * The camera stream from the check is reused for the recording, so the browser does not ask for permission twice.
 */
function VideoKycPage() {
  const { token } = useParams();

  const [phase, setPhase] = useState("loading"); // loading | load-error | blocked | check | ready | done
  const [blocked, setBlocked] = useState("invalid");
  const [kyc, setKyc] = useState({});
  const [error, setError] = useState("");

  // camera + microphone check
  const [checking, setChecking] = useState(false); // waiting for the browser's permission prompt
  const [checkStarted, setCheckStarted] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [cameraOk, setCameraOk] = useState(false);
  const [micOk, setMicOk] = useState(false);
  const [level, setLevel] = useState(0);
  const [cameraHint, setCameraHint] = useState("");
  const [micHint, setMicHint] = useState("");
  const [locationOk, setLocationOk] = useState(false);
  const [locationHint, setLocationHint] = useState("");

  // recording
  const [cameraOn, setCameraOn] = useState(false);
  const [recording, setRecording] = useState(false);
  const [hasRecording, setHasRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const analysisRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const blobRef = useRef(null);
  const previewUrlRef = useRef(null);
  const timerRef = useRef(null);
  const locationRef = useRef(null); // the customer's latest position, sent with the video
  const locationAskRef = useRef(0); // numbers the asks, so the answer to an earlier "Test again" is ignored

  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const stopAnalysis = () => {
    const analysis = analysisRef.current;
    if (!analysis) return;
    clearInterval(analysis.timer);
    if (analysis.ctx && analysis.ctx.state !== "closed" && analysis.ctx.close) {
      Promise.resolve(analysis.ctx.close()).catch(() => {});
    }
    analysisRef.current = null;
  };

  const load = async () => {
    setPhase("loading");
    const resp = await videoKycInfo(token);
    if (!resp || resp.Status !== 1) {
      setPhase("load-error");
      return;
    }
    if (resp.Data?.state !== "active") {
      setBlocked(resp.Data?.state);
      setPhase("blocked");
      return;
    }
    setKyc(resp.Data.kyc || {});
    setPhase("check");
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // the link must not be indexed or leaked through the Referer header
  useEffect(() => {
    const tags = [
      ["robots", "noindex,nofollow"],
      ["referrer", "no-referrer"],
    ].map(([name, content]) => {
      const el = document.createElement("meta");
      el.name = name;
      el.content = content;
      document.head.appendChild(el);
      return el;
    });
    return () => tags.forEach((el) => el.remove());
  }, []);

  useEffect(
    () => () => {
      locationAskRef.current += 1;
      stopAnalysis();
      stopTracks();
      clearInterval(timerRef.current);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  // ------------------------------------------------------------------------------------------ camera + microphone check

  // Watches the live stream: the microphone has to pick up speech and the camera has to show a real picture.
  const beginAnalysis = (stream) => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    let ctx = null;
    let analyser = null;
    let samples = null;
    if (AudioCtx && stream.getAudioTracks().length > 0) {
      try {
        ctx = new AudioCtx();
        if (ctx.resume) ctx.resume();
        analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        ctx.createMediaStreamSource(stream).connect(analyser);
        samples = new Uint8Array(analyser.fftSize);
      } catch (e) {
        analyser = null;
      }
    }
    if (!analyser) {
      setMicHint("Your browser cannot test the microphone. Please open this link in Chrome or Safari.");
    }

    const canvas = document.createElement("canvas");
    canvas.width = 32;
    canvas.height = 24;
    const canvasCtx = canvas.getContext("2d");

    const startedAt = Date.now();
    let loudSamples = 0;
    let brightFrames = 0;
    let micDone = false;
    let cameraDone = false;

    const timer = setInterval(() => {
      const elapsed = Date.now() - startedAt;

      if (analyser && !micDone) {
        analyser.getByteTimeDomainData(samples);
        const loudness = micLevel(samples);
        setLevel(Math.min(1, loudness * 6));
        if (loudness > MIC_SPEECH_LEVEL) loudSamples += 1;
        if (loudSamples >= MIC_SPEECH_TICKS) {
          micDone = true;
          setMicOk(true);
          setMicHint("");
        } else if (elapsed > MIC_HINT_AFTER_MS) {
          setMicHint('We cannot hear you. Make sure your microphone is not muted, then say "Hello" loudly.');
        }
      } else if (analyser) {
        analyser.getByteTimeDomainData(samples); // keep the level bar moving after the microphone passed
        setLevel(Math.min(1, micLevel(samples) * 6));
      }

      if (!cameraDone) {
        const video = videoRef.current;
        if (video && video.videoWidth > 0 && video.readyState >= 2 && canvasCtx) {
          canvasCtx.drawImage(video, 0, 0, 32, 24);
          const brightness = averageBrightness(canvasCtx.getImageData(0, 0, 32, 24).data);
          brightFrames = brightness >= CAMERA_MIN_BRIGHTNESS ? brightFrames + 1 : 0;
          if (brightFrames >= CAMERA_GOOD_FRAMES) {
            cameraDone = true;
            setCameraOk(true);
            setCameraHint("");
          } else if (elapsed > CAMERA_HINT_AFTER_MS) {
            setCameraHint("Your camera picture is very dark. Uncover the camera or move to a brighter place.");
          }
        } else if (elapsed > CAMERA_HINT_AFTER_MS) {
          setCameraHint("Your camera is not sending any picture. Close other apps that use the camera and press Test again.");
        }
      }
    }, CHECK_INTERVAL_MS);

    analysisRef.current = { timer, ctx };
  };

  const startCheck = async () => {
    stopAnalysis();
    stopTracks();
    setCheckStarted(false);
    setCheckError("");
    setCameraOk(false);
    setMicOk(false);
    setLevel(0);
    setCameraHint("");
    setMicHint("");
    setLocationOk(false);
    setLocationHint("");
    locationRef.current = null;
    locationAskRef.current += 1;

    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setCheckError("Your browser does not support video recording. Please open this link in Chrome or Safari.");
      return;
    }

    // The location is asked together with the camera and microphone (the browser shows its own permission prompt).
    const ask = locationAskRef.current;
    getLocation()
      .then((position) => {
        if (locationAskRef.current !== ask) return;
        locationRef.current = position;
        setLocationOk(true);
      })
      .catch((e) => {
        if (locationAskRef.current !== ask) return;
        setLocationHint(locationErrorMessage(e));
      });

    setChecking(true);
    try {
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
      } catch (e) {
        if (e && e.name === "OverconstrainedError") {
          stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } else {
          throw e;
        }
      }
      streamRef.current = stream;

      stream.getTracks().forEach((track) => {
        track.onended = () => {
          setCameraOk(false);
          setMicOk(false);
          setCheckError("Your camera or microphone was disconnected. Please reconnect it and press Test again.");
        };
      });

      const video = videoRef.current;
      video.srcObject = stream;
      video.muted = true;
      video.controls = false;
      setCheckStarted(true);
      beginAnalysis(stream);
    } catch (e) {
      setCheckError(mediaErrorMessage(e));
    } finally {
      setChecking(false);
    }
  };

  const continueToRecording = () => {
    stopAnalysis();
    setError("");
    setPhase("ready");
  };

  // entering the recording step: use the camera that was just tested
  useEffect(() => {
    if (phase !== "ready") return;
    const stream = streamRef.current;
    if (stream && videoRef.current && stream.getTracks().some((t) => t.readyState === "live")) {
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.controls = false;
      setCameraOn(true);
    }
  }, [phase]);

  // ------------------------------------------------------------------------------------------ recording

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
  };

  useEffect(() => {
    if (recording && seconds >= MAX_SECONDS) stopRecording();
  }, [recording, seconds]);

  // Throws the recording away without the "recording finished" handler running (the page is about to be replaced).
  const abandonRecording = () => {
    clearInterval(timerRef.current);
    const recorder = recorderRef.current;
    if (recorder) {
      recorder.ondataavailable = null;
      recorder.onstop = null;
      if (recorder.state !== "inactive") recorder.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];
    blobRef.current = null;
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setRecording(false);
    setHasRecording(false);
  };

  // The link can stop working while this page is open: a newer link was sent for the lead (only the newest link works),
  // the hour ran out, or the video was already submitted from another window. Ask again every few seconds, and when the
  // customer comes back to this tab. When the link is no longer usable the camera and microphone are switched off and the
  // customer is told why, instead of recording a video that could not be saved. A request that fails (network) never
  // locks the customer out.
  useEffect(() => {
    if ((phase !== "check" && phase !== "ready") || submitting) return undefined;
    let cancelled = false;
    let asking = false;

    const verify = async () => {
      if (asking) return;
      asking = true;
      try {
        const resp = await videoKycInfo(token);
        const state = resp?.Data?.state;
        if (cancelled || resp?.Status !== 1 || !BLOCKED_STATES.includes(state)) return;
        abandonRecording();
        stopAnalysis();
        stopTracks();
        setBlocked(state);
        setPhase("blocked");
      } finally {
        asking = false;
      }
    };

    const timer = setInterval(verify, LINK_CHECK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") verify();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, token, submitting]);

  // only used when the tested camera is no longer live (for example the customer waited a long time)
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
      streamRef.current = stream;
      videoRef.current.srcObject = stream;
      videoRef.current.muted = true;
      videoRef.current.controls = false;
      setCameraOn(true);
      setError("");
    } catch (e) {
      setError(mediaErrorMessage(e));
    }
  };

  const startRecording = () => {
    chunksRef.current = [];
    const mimeType = MIME_CANDIDATES.find((t) => window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported(t));

    let recorder;
    try {
      recorder = new window.MediaRecorder(streamRef.current, {
        ...(mimeType ? { mimeType } : {}),
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
    } catch (e) {
      setError("Unable to start recording on this browser.");
      return;
    }

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };
    recorder.onstop = () => {
      blobRef.current = new Blob(chunksRef.current, { type: recorder.mimeType || "video/webm" });
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = URL.createObjectURL(blobRef.current);

      const video = videoRef.current;
      video.srcObject = null;
      video.muted = false;
      video.controls = true;
      video.src = previewUrlRef.current;

      setRecording(false);
      setHasRecording(true);
    };

    recorderRef.current = recorder;
    recorder.start();
    getLocation()
      .then((position) => {
        locationRef.current = position;
      })
      .catch(() => {}); // keep the position from the check
    setError("");
    setSeconds(0);
    setRecording(true);
    timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
  };

  const retake = () => {
    blobRef.current = null;
    setHasRecording(false);
    setError("");
    const video = videoRef.current;
    video.controls = false;
    video.muted = true;
    video.removeAttribute("src");
    video.srcObject = streamRef.current;
  };

  const submit = async () => {
    if (!blobRef.current) {
      setError("Please record a video first.");
      return;
    }
    setSubmitting(true);
    setUploadPct(0);
    setError("");

    const ext = blobRef.current.type.includes("mp4") ? "mp4" : "webm";
    const resp = await videoKycSubmit(token, blobRef.current, ext, setUploadPct, locationRef.current);
    setSubmitting(false);

    if (resp?.Status === 1) {
      stopTracks();
      setPhase("done");
    } else if (BLOCKED_STATES.includes(resp?.Data?.state)) {
      stopTracks();
      setBlocked(resp.Data.state);
      setPhase("blocked");
    } else {
      setError(resp?.Message || "Upload failed. Please check your internet connection and try again.");
    }
  };

  const allWorking = cameraOk && micOk && locationOk;

  return (
    <VideoKycWrapper>
      <div className="container">
        <h2>📹 Video KYC</h2>

        {phase === "loading" && <div className="msg center">Loading...</div>}

        {phase === "load-error" && (
          <>
            <div className="msg error center">We could not load this page. Please check your internet connection and try again.</div>
            <button onClick={load}>Try again</button>
          </>
        )}

        {phase === "blocked" && (
          <div className={`msg center ${blocked === "already_done" ? "success" : "error"}`}>
            {BLOCKED_MESSAGES[blocked] || BLOCKED_MESSAGES.invalid}
          </div>
        )}

        {phase === "done" && <div className="msg success center">Your Video KYC has been submitted successfully. Thank you!</div>}

        {phase === "check" && (
          <>
            <div className="highlight">
              <p>
                <strong>Step 1 of 2:</strong> let us check your camera, microphone and location.
              </p>
              <p>
                Press the button, allow access when your browser asks, then say <strong>&ldquo;Hello&rdquo;</strong> out loud.
              </p>
              <p>Your location is saved with your video, only for this verification.</p>
            </div>

            {checkError && <div className="msg error">{checkError}</div>}

            <video ref={videoRef} aria-label="Camera preview" autoPlay playsInline muted style={{ display: checkStarted ? "block" : "none" }} />

            {checkStarted && (
              <>
                <ul className="checklist">
                  <li>{cameraOk ? "✅ Camera is working" : "⏳ Checking your camera..."}</li>
                  <li>{micOk ? "✅ Microphone is working" : '⏳ Checking your microphone... say "Hello"'}</li>
                  <li>{locationOk ? "✅ Location is working" : locationHint ? "❌ Location is not available" : "⏳ Getting your location..."}</li>
                </ul>
                <div className="meter" role="progressbar" aria-label="Microphone level" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(level * 100)}>
                  <div className="bar" style={{ width: `${Math.round(level * 100)}%` }} />
                </div>
                {!cameraOk && cameraHint && <div className="hint">{cameraHint}</div>}
                {!micOk && micHint && <div className="hint">{micHint}</div>}
                {!locationOk && locationHint && <div className="hint">{locationHint}</div>}
              </>
            )}

            <div className="button-row">
              {!checkStarted ? (
                <button onClick={startCheck} disabled={checking}>
                  {checking ? "Waiting for permission..." : "Test camera, microphone and location"}
                </button>
              ) : (
                <>
                  <button onClick={continueToRecording} disabled={!allWorking}>
                    Continue to Video KYC
                  </button>
                  <button className="secondary" onClick={startCheck} disabled={checking}>
                    Test again
                  </button>
                </>
              )}
            </div>
          </>
        )}

        {phase === "ready" && (
          <>
            <div className="note">
              <strong>Step 2 of 2:</strong> record your video.
            </div>

            <div className="highlight">
              <p>
                Hello! My name is <strong>{kyc.name || ""}</strong>.
              </p>
              <p>
                I am here to complete the video KYC process for my loan application with <strong>KuberMax</strong>.
              </p>
              <p>
                My PAN number is <strong>{kyc.pancard || "-"}</strong> (please show your PAN card clearly to the camera).
              </p>
              <p>
                My loan amount is ₹<strong>{kyc.loan_recommended || "-"}</strong>, disbursal amount ₹
                <strong>{kyc.net_disbursal_amount || "-"}</strong>.
              </p>
              <p>
                Repayment amount ₹<strong>{kyc.repayment_amount || "-"}</strong>, repayment date{" "}
                <strong>{kyc.repayment_date || "-"}</strong>.
              </p>
            </div>

            <div className="note">
              ✅ Keep your face and PAN card clearly visible.
              <br />
              ✅ Record in a well-lit, quiet place.
              <br />✅ Speak clearly and at a normal volume so your audio is clear.
              <br />✅ Maximum recording length: {MAX_SECONDS} seconds.
              <br />✅ Your location is saved with your video.
            </div>

            {error && <div className="msg error">{error}</div>}

            <video ref={videoRef} aria-label="Camera preview" autoPlay playsInline muted />

            {recording && <div className="timer">{formatTimer(seconds)}</div>}

            <div className="button-row">
              {!cameraOn && <button onClick={startCamera}>Enable Camera</button>}
              {cameraOn && !recording && !hasRecording && <button onClick={startRecording}>Start Recording</button>}
              {recording && (
                <button className="danger" onClick={stopRecording}>
                  Stop Recording
                </button>
              )}
              {hasRecording && !submitting && (
                <>
                  <button className="secondary" onClick={retake}>
                    Retake
                  </button>
                  <button onClick={submit}>Submit Video KYC</button>
                </>
              )}
              {submitting && <button disabled>{uploadPct > 0 ? `Uploading... ${uploadPct}%` : "Uploading..."}</button>}
            </div>
          </>
        )}
      </div>
    </VideoKycWrapper>
  );
}

export default VideoKycPage;
